// ============================================================
// NotificationPermissionPrompt — ブラウザ通知許可プロンプト
//
// 業務用アプリのため、通知が許可されるまで繰り返し表示する。
// - 'default': カスタムダイアログ → ネイティブ許可ダイアログ
// - 'denied': ブラウザ設定への案内を表示し続ける
// - 'granted': 非表示
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';

const DISMISS_KEY = 'notification_prompt_dismissed_at';
const DISMISS_INTERVAL_MS = 10 * 60 * 1000; // denied 時は10分ごとに再表示

export default function NotificationPermissionPrompt() {
  const [visible, setVisible] = useState(false);
  const [permState, setPermState] = useState<NotificationPermission | 'unsupported'>('default');

  useEffect(() => {
    if (typeof Notification === 'undefined') {
      setPermState('unsupported');
      return;
    }

    const perm = Notification.permission;
    setPermState(perm);

    if (perm === 'granted') {
      setVisible(false);
      return;
    }

    // denied の場合: 前回非表示にしてから一定時間経過で再表示
    if (perm === 'denied') {
      const dismissedAt = localStorage.getItem(DISMISS_KEY);
      if (dismissedAt && Date.now() - Number(dismissedAt) < DISMISS_INTERVAL_MS) {
        setVisible(false);
        return;
      }
    }

    // 1秒後に表示（ページ読み込み直後のちらつき防止）
    const timer = setTimeout(() => setVisible(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  const handleAllow = useCallback(async () => {
    if (typeof Notification === 'undefined') return;

    try {
      const result = await Notification.requestPermission();
      setPermState(result);
      if (result === 'granted') {
        setVisible(false);
        // テスト通知
        new Notification('通知が有効になりました', {
          body: '予約の更新通知を受け取れます',
          icon: '/favicon.ico',
        });
      } else if (result === 'denied') {
        // ブロックされた → 案内を表示し続ける
        setPermState('denied');
      }
    } catch (e) {
      console.error('[NotificationPermissionPrompt] error:', e);
    }
  }, []);

  const handleDismiss = useCallback(() => {
    setVisible(false);
    if (permState === 'denied') {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    }
  }, [permState]);

  if (!visible || permState === 'granted' || permState === 'unsupported') return null;

  const isDenied = permState === 'denied';

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 99999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(0,0,0,0.5)',
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 12,
        padding: '28px 32px',
        maxWidth: 420,
        width: '90%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>
          {isDenied ? '🔇' : '🔔'}
        </div>
        <h2 style={{ margin: '0 0 8px', fontSize: 18, color: '#1e293b' }}>
          {isDenied ? '通知がブロックされています' : 'ブラウザ通知を許可してください'}
        </h2>
        <p style={{ margin: '0 0 20px', fontSize: 14, color: '#64748b', lineHeight: 1.6 }}>
          {isDenied ? (
            <>
              予約の更新通知を受け取るには、ブラウザの設定から通知を許可してください。
              <br /><br />
              <strong>設定方法:</strong><br />
              アドレスバー左の🔒アイコン → 「通知」→「許可」に変更
            </>
          ) : (
            <>
              予約の変更やお知らせをリアルタイムで受け取るために、
              ブラウザの通知を許可してください。
              <br />
              <span style={{ fontSize: 12, color: '#94a3b8' }}>
                業務に必要な通知のみ送信されます。
              </span>
            </>
          )}
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
          {!isDenied && (
            <button
              onClick={handleAllow}
              style={{
                padding: '10px 28px',
                background: '#3b82f6',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              許可する
            </button>
          )}
          <button
            onClick={handleDismiss}
            style={{
              padding: '10px 28px',
              background: isDenied ? '#3b82f6' : 'transparent',
              color: isDenied ? '#fff' : '#94a3b8',
              border: isDenied ? 'none' : '1px solid #e2e8f0',
              borderRadius: 8,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            {isDenied ? '閉じる（10分後に再表示）' : '後で'}
          </button>
        </div>
      </div>
    </div>
  );
}
